import { FC, useEffect, useMemo, useRef } from 'react'
import { ThemeProvider } from 'styled-components'
import { useLocation } from 'react-router-dom'
import { lightTheme as theme, backofficeTheme } from './theme'
import GlobalStyle from './GlobalStyle'
import Routes from './routes'
import { useAppDispatch, useAppSelector } from './store/hooks'
import urls from './routes/urls'
import ScrollRestoration from './shared/ScrollRestoration'
import programs from './programs'
import type { ProgramCodeUrlParam } from './programs/programs.types'
import { updateLanguage } from './store/slices/settings'

const App: FC = _props => {
  const mounted = useRef(false)
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { language } = useAppSelector(state => state.settings)
  const isBackofficeRoute = location.pathname.includes(urls.backoffice)

  /** Array of top level paths which are not programs */
  const programBlacklist = useMemo(() => [urls.backoffice, urls.auth, urls.notFound], [])

  /** Array of top level paths which are programs  */
  const programWhitelist = useMemo(() => Object.keys(programs), [])

  /** Retrieves the Program param from the pathname */
  const program = useMemo(() => location.pathname.split('/')[1], [location]) as
    | ProgramCodeUrlParam
    | 'admin'
    | 'authentication'
    | '404'
    | undefined

  /**
   * If the current persisted language is not supported by the current program, change it to the default language.
   * This can happen when switching between programs that have different supported languages.
   * - Ignore this check for the top level paths that are not programs.
   */
  useEffect(() => {
    if (!mounted.current && program && !programBlacklist.includes(program) && programWhitelist.includes(program)) {
      if (!programs[program].supportedLanguages.includes(language)) {
        dispatch(updateLanguage(programs[program].defaultLanguage))
      }
    }
    if (program) {
      mounted.current = true
    }
  }, [dispatch, language, program, programBlacklist, programWhitelist])

  return (
    <ThemeProvider theme={isBackofficeRoute ? backofficeTheme : theme}>
      <GlobalStyle theme={isBackofficeRoute ? backofficeTheme : theme} isBackoffice={isBackofficeRoute} />
      <Routes />
      <ScrollRestoration />
    </ThemeProvider>
  )
}

export default App
